import { Link } from "react-router-dom";
import {
  ArrowUpRight, ArrowRight, Search, Map, Hammer, ClipboardCheck,
  Rocket, ShieldCheck, TrendingUp, Crown, Sparkles, RefreshCw, FileCheck2,
  Users, Home as HomeIcon, Baby, Brain, Activity, Compass, Building2, HeartPulse,
} from "lucide-react";

const SERVICES = [
  {
    icon: Search,
    stage: "Stage 01",
    title: "Discover",
    tagline: "Understand before we advise.",
    desc: "We begin every engagement with deep discovery, understanding your vision, market, experience and ambitions before we recommend anything.",
    bullets: ["Discovery Workshop", "Provider Readiness Assessment™", "Business Health Check", "Registration Pathway Assessment"],
  },
  {
    icon: Map,
    stage: "Stage 02",
    title: "Plan",
    tagline: "Create the roadmap.",
    desc: "Before writing a single policy, we design your business. Strategy, model, pricing and roadmaps come first.",
    bullets: ["Business Strategy", "Service Offering & Pricing", "Business Model", "Operational & Compliance Roadmap"],
  },
  {
    icon: Hammer,
    stage: "Stage 03",
    title: "Build",
    tagline: "Build the business, not just the paperwork.",
    desc: "This is where Elevate becomes different. We build the operating system of your provider, end to end.",
    bullets: ["Policies & Procedures", "Governance Frameworks", "HR & Quality Systems", "Forms, Registers & Operational Manuals"],
  },
  {
    icon: ClipboardCheck,
    stage: "Stage 04",
    title: "Register",
    tagline: "Navigate registration with confidence.",
    desc: "Full registration management from application through Stage 1 and 2 audit readiness.",
    bullets: ["Application Support", "Documentation Review", "Audit Preparation & Mock Audit", "Evidence Library & Auditor Liaison"],
  },
  {
    icon: Rocket,
    stage: "Stage 05",
    title: "Launch",
    tagline: "Turn approval into a thriving business.",
    desc: "Registration is the beginning. We help providers launch with momentum and the right systems in place.",
    bullets: ["Client Onboarding Systems", "Service Agreements", "Website & Marketing Review", "Referral Strategy & Operations Review"],
  },
  {
    icon: ShieldCheck,
    stage: "Stage 06",
    title: "Protect",
    tagline: "Stay compliant. Stay confident.",
    desc: "Elevate Protect™, our ongoing compliance partnership for providers who want to stay ahead of the curve.",
    bullets: ["Policy & Legislative Updates", "Internal Audits & Reviews", "Governance Advice", "Unlimited Support (subject to membership terms)"],
    badge: "Elevate Protect™",
  },
  {
    icon: TrendingUp,
    stage: "Stage 07",
    title: "Grow",
    tagline: "Build a stronger organisation.",
    desc: "When you're ready to scale, we help you expand services, develop leaders and build operational efficiency.",
    bullets: ["New Registration Groups", "Operational Efficiency", "Business Coaching", "Expansion Planning & Team Development"],
  },
  {
    icon: Crown,
    stage: "Stage 08",
    title: "Lead",
    tagline: "Create a lasting impact.",
    desc: "The final stage focuses on building organisations recognised for quality, leadership and participant outcomes.",
    bullets: ["Governance Maturity", "Leadership Capability", "Organisational Culture", "Long Term Growth Readiness"],
  },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      {/* HEADER */}
      <section className="container-pad pt-16 pb-12">
        <div className="overline">Our services</div>
        <h1 className="font-display text-5xl sm:text-6xl text-navy mt-6 tracking-tight max-w-4xl leading-[1.05]">
          Every service mapped to <span className="text-brand-gradient font-serif-mark italic">The Elevate Method™</span>.
        </h1>
        <p className="text-muted2 mt-8 max-w-2xl leading-relaxed text-lg">
          We don't sell disconnected packages. Our services follow the eight stages of The Elevate Method™, the same
          framework every Elevate client moves through. Pick a single stage or partner with us for the full journey.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-4">
          <Link to="/method" data-testid="services-method-cta" className="btn-ochre">
            See The Elevate Method™ <ArrowUpRight size={16} />
          </Link>
          <Link to="/contact" className="btn-ghost-navy">
            Book free Discovery
          </Link>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="container-pad pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s, idx) => (
            <article key={s.title} data-testid={`service-${idx}`} className="card-soft p-8 flex flex-col">
              <div className="flex items-start justify-between">
                <div className="w-12 h-12 rounded-xl bg-teal/10 flex items-center justify-center">
                  <s.icon size={22} className="text-teal" />
                </div>
                <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-teal">{s.stage}</span>
              </div>
              <h3 className="font-display text-xl text-navy mt-6">{s.title}</h3>
              <p className="font-serif-mark italic text-teal mt-1">{s.tagline}</p>
              <p className="text-muted2 mt-3 leading-relaxed text-sm">{s.desc}</p>
              {s.badge && (
                <div className="mt-4 inline-flex self-start items-center gap-2 bg-teal/10 text-teal px-3 py-1 rounded-full text-[10px] font-semibold tracking-wide uppercase">
                  {s.badge}
                </div>
              )}
              <ul className="mt-5 space-y-2 flex-1">
                {s.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-ink">
                    <span className="w-1.5 h-1.5 bg-teal mt-2 rounded-full flex-shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      {/* REGISTRATION AUDIT READINESS - NDIS PRACTICE STANDARDS MODULES */}
      <section className="container-pad pb-24">
        <div className="max-w-3xl">
          <div className="overline">Registration audit readiness</div>
          <h2 className="font-display text-4xl sm:text-5xl text-navy mt-4 tracking-tight leading-[1.08]">
            Prepared for every module of the <span className="text-brand-gradient font-serif-mark italic">NDIS Practice Standards</span>.
          </h2>
          <p className="text-muted2 mt-6 leading-relaxed text-lg">
            Your registration audit is scoped by the NDIS Commission based on the supports you deliver. Whatever your
            scope, Elevate gets you ready for every module that applies to your organisation.
          </p>
        </div>

        {/* CORE MODULE - FEATURED */}
        <div className="mt-14 bg-brand-gradient text-canvas rounded-3xl p-10 md:p-14 relative overflow-hidden grain">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 relative">
            <div className="md:col-span-5">
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 px-3 py-1.5 rounded-full text-[10px] font-bold tracking-widest uppercase">
                Applies to all high risk providers
              </div>
              <h3 className="font-display text-4xl mt-6 tracking-tight">Core Module</h3>
              <p className="font-serif-mark italic text-2xl mt-2" style={{ color: '#A7E0E6' }}>The foundation of every certification audit.</p>
              <p className="text-canvas/85 mt-6 leading-relaxed">
                Applies to every provider delivering higher risk supports. We take you through it end to end.
              </p>
            </div>
            <div className="md:col-span-7">
              <div className="grid grid-cols-2 gap-3">
                {[
                  "Rights & Responsibilities",
                  "Governance & Operational Management",
                  "Provision of Supports",
                  "Provider Environment",
                ].map((t) => (
                  <div key={t} className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-xl p-5 flex items-center">
                    <div className="font-display text-lg">{t}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* VERIFICATION MODULE */}
        <div className="mt-6 card-soft p-10 md:p-14">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-5">
              <div className="inline-flex items-center gap-2 bg-teal/10 text-teal px-3 py-1.5 rounded-full text-[10px] font-bold tracking-widest uppercase">
                Applies to lower risk providers
              </div>
              <h3 className="font-display text-4xl text-navy mt-6 tracking-tight">Verification Module</h3>
              <p className="font-serif-mark italic text-2xl text-teal mt-2">A streamlined pathway for lower risk supports.</p>
              <p className="text-muted2 mt-6 leading-relaxed">
                A document based verification against a targeted set of standards. We prepare the whole pack.
              </p>
            </div>
            <div className="md:col-span-7 flex items-center">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full">
                {["Human Resources", "Risk Management", "Complaints & Incidents"].map((t) => (
                  <div key={t} className="bg-sand rounded-xl p-5 flex items-center">
                    <div className="font-display text-navy">{t}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* SUPPLEMENTARY MODULES */}
        <div className="mt-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
            <div>
              <div className="overline">Supplementary modules</div>
              <h3 className="font-display text-3xl sm:text-4xl text-navy mt-4 tracking-tight leading-[1.08] max-w-2xl">
                Additional modules for higher risk supports.
              </h3>
              <p className="text-muted2 mt-4 max-w-2xl leading-relaxed">
                Depending on your registration groups, one or more supplementary modules may apply on top of the Core Module.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: HeartPulse,  t: "High Intensity Daily Personal Activities" },
              { icon: Brain,       t: "Specialist Behaviour Support" },
              { icon: Activity,    t: "Implementing Behaviour Support Plans" },
              { icon: Baby,        t: "Early Childhood Supports" },
              { icon: Compass,     t: "Specialist Support Coordination" },
              { icon: Building2,   t: "Specialist Disability Accommodation (SDA)" },
              { icon: HomeIcon,    t: "Assistance with Supported Independent Living (SIL)" },
            ].map((m) => (
              <div key={m.t} className="card-soft p-6 flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-teal/10 flex items-center justify-center flex-shrink-0">
                  <m.icon size={20} className="text-teal" />
                </div>
                <div className="font-display text-navy leading-snug">{m.t}</div>
              </div>
            ))}
          </div>
        </div>

        {/* HOW WE PREPARE YOU */}
        <div className="mt-16 card-soft p-10 md:p-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-7">
              <div className="overline">How we prepare you</div>
              <h3 className="font-display text-3xl text-navy mt-4 tracking-tight leading-[1.1]">
                Whatever your scope, we get you audit ready.
              </h3>
              <p className="text-muted2 mt-6 leading-relaxed">
                Every provider's scope of audit is different. We assess what applies to you, build the systems and
                evidence you'll need, and stay in the room with you through audit day. The specifics of your engagement
                are shaped in Discovery.
              </p>
            </div>
            <div className="md:col-span-5 md:text-right">
              <Link to="/contact" data-testid="audit-readiness-cta" className="btn-ochre">
                Book audit readiness call <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>


      {/* FOCUSED ENGAGEMENTS */}
      <section className="bg-sand py-24">
        <div className="container-pad">
          <div className="max-w-2xl">
            <div className="overline">Focused engagements</div>
            <h2 className="font-display text-4xl sm:text-5xl text-navy mt-4 tracking-tight leading-[1.08]">
              Need help with something specific?
            </h2>
            <p className="text-muted2 mt-6 leading-relaxed">
              Point services for providers who need targeted expertise. Each one delivered by a senior consultant.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
            {[
              { icon: FileCheck2,     t: "New Registration",       d: "Full application and audit preparation for first time providers." },
              { icon: RefreshCw,      t: "Registration Renewal",   d: "Renewal audit preparation and evidence refresh for existing providers." },
              { icon: ClipboardCheck, t: "Audit Coaching",         d: "Structured one on one coaching to prepare your team for audit day." },
              { icon: ShieldCheck,    t: "Corrective Action Plans",d: "Support after non conformity findings, including close out audit prep." },
              { icon: Sparkles,       t: "Regulator Response",     d: "Guidance responding to NDIS Commission enquiries or requests." },
              { icon: ArrowRight,     t: "Registration Change",    d: "Variations to your registration, including adding or removing supports." },
            ].map((s) => (
              <div key={s.t} className="card-soft p-6 flex flex-col">
                <div className="w-11 h-11 rounded-xl bg-teal/10 flex items-center justify-center">
                  <s.icon size={20} className="text-teal" />
                </div>
                <div className="font-display text-lg text-navy mt-5">{s.t}</div>
                <p className="text-muted2 text-sm mt-2 leading-relaxed flex-1">{s.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/contact" data-testid="focused-services-cta" className="btn-ghost-navy">
              Discuss your engagement <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>


      {/* HOW WE ENGAGE */}
      <section className="bg-sand py-24">
        <div className="container-pad grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="overline">How we engage</div>
            <h2 className="font-display text-4xl text-navy mt-4 tracking-tight leading-[1.1]">
              Three ways to work with us.
            </h2>
            <p className="text-muted2 mt-6 leading-relaxed">
              We meet providers where they are. Most clients start with a Discovery Workshop and choose the right engagement together.
            </p>
            <Link to="/contact" data-testid="services-book-cta" className="btn-ghost-navy mt-8">
              Book Discovery <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="md:col-span-7 space-y-4">
            {[
              { icon: FileCheck2, t: "Fixed fee project",        d: "A clear scope, fixed price and predictable timeline. Perfect for registration and one off engagements." },
              { icon: Sparkles,   t: "Hourly advisory",          d: "On demand expertise for specific challenges: incident response, policy edits, audit queries." },
              { icon: RefreshCw,  t: "Elevate Protect™ membership", d: "Ongoing compliance partnership with quarterly reviews, unlimited consultant access and proactive support." },
            ].map((x) => (
              <div key={x.t} className="card-soft p-6 flex items-start gap-5">
                <div className="w-12 h-12 rounded-xl bg-teal/10 flex items-center justify-center flex-shrink-0">
                  <x.icon size={22} className="text-teal" />
                </div>
                <div>
                  <div className="font-display text-xl text-navy">{x.t}</div>
                  <p className="text-muted2 mt-2 text-sm leading-relaxed">{x.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-pad py-24">
        <div className="bg-brand-gradient text-canvas rounded-3xl p-12 grid grid-cols-1 md:grid-cols-12 gap-6 items-center grain relative overflow-hidden">
          <div className="md:col-span-8">
            <h3 className="font-display text-3xl tracking-tight">Not sure which stage you need?</h3>
            <p className="text-canvas/85 mt-3">Start with Discover. A free 30 minute workshop maps your provider pathway and the right next step.</p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/contact" data-testid="services-cta" className="btn-light-on-navy">
              Book Discovery <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
